import React, { useState, useEffect } from 'react';
import { array, bool, number } from 'prop-types';
import { isEmpty, uniq } from 'lodash';

import MaterialTable from 'material-table';
import { TablePagination } from '@material-ui/core';

import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import Switch from '../../components/FormFields/Switch';

import classes from './NiagaraDevicesTable.module.css';

const TechniciansTable = ({
    isLoading,
    onPageSet,
    onPageSizeSet,
    page,
    pageSize,
    selectedAgent,
    setTechnicianSearch,
    technicians,
    techniciansLoading,
    techniciansMeta,
    technicianTrackingLoading,
    updateErpTechnician
}) => {
    const [selectedTechnicians, setSelectedTechnicians] = useState([]);
    const [techToggles, setTechToggles] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [toggledTechnicians, setToggledTechnicians] = useState([]);

    useEffect(() => {
        setToggledTechnicians(selectedAgent.filteredTechnicians);
    }, [selectedAgent]);

    useEffect(() => {
        technicians.forEach(technician => {
            setTechToggles({...techToggles, [technician.id]: false});
        });
    }, []);

    useEffect(() => {
        if (techniciansMeta.totalCount && techniciansMeta.totalCount !== totalCount) {
            setTotalCount(techniciansMeta.totalCount);
        }
    }, [techniciansMeta]);

    const techIdSorter = (a, b) => {
        return sorter(a, b, 'sourceId');
    };

    const nameSorter = (a, b) => {
        const aCopy = {...a};
        const bCopy = {...b};

        if (!aCopy.firstName) aCopy.firstName = '';
        if (!bCopy.firstName) bCopy.firstName = '';

        return sorter(aCopy, bCopy, 'firstName');
    };

    const titleSorter = (a, b) => {
        const aCopy = {...a};
        const bCopy = {...b};

        if (!aCopy.title) aCopy.title = '';
        if (!bCopy.title) bCopy.title = '';

        return sorter(aCopy, bCopy, 'title');
    };

    const trackedSorter = (a, b) => {
        const aTracked = selectedAgent.filteredTechnicians.includes(a.id);
        const bTracked = selectedAgent.filteredTechnicians.includes(b.id);

        if (aTracked < bTracked) {
            return -1;
        }
        if (aTracked > bTracked) {
            return 1;
        }

        return 0;
    };

    const sorter = (a, b, property) => {
        const valueA = a[property].toUpperCase();
        const valueB = b[property].toUpperCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const handleSearch = searchValue => {
        setTechnicianSearch(searchValue);
    };

    const handleSelectionChange = rows => {
        setSelectedTechnicians([...rows]);
    };

    const handleStartTracking = async () => {
        if (!isEmpty(selectedTechnicians)) {
            const alreadyTracked = technicians.map(technician => toggledTechnicians.find(id => id === technician.id)).filter(Boolean);
            const newIds = selectedTechnicians.map(technician => technician.id);
            const idsToTrack = [...alreadyTracked, ...newIds];
            const payload = uniq(idsToTrack);

            const toggles = {};
            newIds.forEach(id => {
                toggles[id] = true;
            });
            setTechToggles({...techToggles, ...toggles});

            await updateErpTechnician({ filteredTechnicians: payload, id: selectedAgent.id });
            setToggledTechnicians(payload);

            newIds.forEach(id => {
                toggles[id] = false;
            });
            setTechToggles({...techToggles, ...toggles});
        }
    };

    const handleStopTracking = async () => {
        if (isEmpty(selectedTechnicians)) {
            return;
        }

        if (!isEmpty(selectedTechnicians)) {
            const technicianIds = technicians.map(technician => technician.id);
            const selectedIds = selectedTechnicians.map(technician => technician.id);
            const idsToTrack = technicianIds.filter(id => !selectedIds.find(newId => id === newId));
            const alreadyTracked = technicians.map(technician => toggledTechnicians.find(id => id === technician.id)).filter(Boolean);
            const uniqueList = uniq(idsToTrack);
            const payload = uniqueList.map(id => alreadyTracked.find(trackedId => trackedId === id)).filter(Boolean);

            const toggles = {};
            selectedIds.forEach(id => {
                toggles[id] = true;
            });
            setTechToggles({...techToggles, ...toggles});

            await updateErpTechnician({ filteredTechnicians: payload, id: selectedAgent.id });
            setToggledTechnicians(payload);

            selectedIds.forEach(id => {
                toggles[id] = false;
            });
            setTechToggles({...techToggles, ...toggles});
        }
    };

    const handleCellToggle = async rowData => {
        let filteredTechnicians = [];

        if (toggledTechnicians && toggledTechnicians.includes(rowData.id)) {
            filteredTechnicians = toggledTechnicians.filter(id => id !== rowData.id);
        } else if (toggledTechnicians) {
            filteredTechnicians = [...toggledTechnicians];
            filteredTechnicians.push(rowData.id);
        }

        setTechToggles({...techToggles, [rowData.id]: true});
        await updateErpTechnician({ filteredTechnicians, id: selectedAgent.id });
        setToggledTechnicians(filteredTechnicians);
        setTechToggles({...techToggles, [rowData.id]: false});
    };

    const handlePageChange = (event, page) => {
        onPageSet(page);
    };

    const handlePageSizeChange = event => {
        const { value } = event.target;

        onPageSizeSet(value);
    };

    const renderCopy = (rowData, value) => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        return rowData[value];
    };

    const renderToggleCell = (rowData, value = 'tracked') => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        let isEnabled = false;
        if (toggledTechnicians && rowData.id) {
            isEnabled = toggledTechnicians.includes(rowData.id);
        }

        return (
            <Switch
                checked={isEnabled}
                disableRipple
                name="enabled"
                onChange={() => handleCellToggle(rowData)}
                isLoading={techToggles[rowData.id]}
            />
        );
    };

    const renderNameCell = ({firstName, lastName}) => {
        if (!firstName && !lastName) return '-';
        const name = `${firstName || ''} ${lastName || ''}`;

        return name.trim();
    };

    return (
        <React.Fragment>
            <div className={classes.NiagaraDevicesTable} >
                <ActionBar
                    onSearch={handleSearch}
                >
                    <ActionBarItem
                        onClick={handleStartTracking}
                        iconSrc="/assets/images/switchEnabledIconColor.svg"
                    >
                        Start Tracking
                    </ActionBarItem>
                    <ActionBarItem
                        onClick={handleStopTracking}
                        iconSrc="/assets/images/switchDisabledIconColor.svg"
                    >
                        Stop Tracking
                    </ActionBarItem>
                </ActionBar>
                <MaterialTable
                    options={{
                        paging:              true,
                        showTitle:           false,
                        search:              false,
                        selection:           true,
                        sorting:             true,
                        toolbar:             false,
                        emptyRowsWhenPaging: false,
                        pageSize
                    }}
                    columns={[
                        {
                            title:      'Technician ID',
                            render:     rowData => renderCopy(rowData, 'sourceId'),
                            customSort: techIdSorter
                        },
                        {
                            title:       'Name',
                            render:      rowData => renderNameCell(rowData),
                            customSort:  nameSorter,
                            defaultSort: 'asc'
                        },
                        {
                            title:      'Title',
                            render:     rowData => renderCopy(rowData, 'title'),
                            customSort: titleSorter
                        },
                        {
                            title:      'Tracked',
                            render:     rowData => renderToggleCell(rowData),
                            customSort: trackedSorter
                        }
                    ]}
                    data={technicians}
                    onSelectionChange={handleSelectionChange}
                    isLoading={isLoading}
                    components={{
                        Pagination: props => {
                            return (
                                <TablePagination
                                    {...props}
                                    count={totalCount}
                                    page={technicians.length ? page : 0}
                                    rowsPerPage={pageSize}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={event => { props.onChangeRowsPerPage(event); handlePageSizeChange(event); } }
                                />
                            );
                        }
                    }}
                />
            </div>
        </React.Fragment>
    );
};

TechniciansTable.defaultProps = {
    page:               0,
    pageSize:           100,
    technicians:        [],
    techniciansLoading: false
};

TechniciansTable.propTypes = {
    page:               number,
    pageSize:           number,
    technicians:        array,
    techniciansLoading: bool
};

export default TechniciansTable;
